import { Form, Formik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Button } from "@volca/ui/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  Dialog,
} from "@volca/ui/components/ui/dialog";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { InfoWithLabel } from "@volca/ui/components/ui/info-with-label";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { IAchAccount } from "@/internal-transactions/InternalTransactions";
import {
  useGetAchAccount,
  useSetAchAccount,
} from "@/internal-transactions/query";

interface NewBankDetailsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string | null;
}

const schema = yup.object().shape({
  account_type: yup.string().required(),
  account_number: yup
    .string()
    .required()
    .matches(/^\d+$/, "Account number must contain only digits"),
  confirm_account_number: yup
    .string()
    .required()
    .matches(/^\d+$/, "Account number must contain only digits")
    .oneOf([yup.ref("account_number")], "Account numbers must match"),
  routing_number: yup
    .string()
    .required()
    .matches(/^\d+$/, "Routing number must contain only digits")
    .length(9, "Routing number must be exactly 9 digits"),
});

const ACCOUNT_TYPE_OPTIONS = [
  { label: "Checking", value: "checking" },
  { label: "Savings", value: "savings" },
];

export const NewBankDetailsModal = ({
  isOpen,
  onOpenChange,
  businessId,
}: NewBankDetailsModalProps) => {
  const [accountTypeSearchValue, setAccountTypeSearchValue] = useState("");

  const { data: achAccount } = useGetAchAccount(businessId);
  const { mutate: setAchAccount } = useSetAchAccount(businessId);

  const overwritingBankDetails = !!achAccount; // if the user already has ach acct info they are overwriting their current info with this form

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogDescription className="sr-only">
          {overwritingBankDetails ? "New bank details" : "Add bank details"}
        </DialogDescription>
        <Formik
          initialValues={{
            account_type: "checking",
            account_number: "",
            confirm_account_number: "",
            routing_number: "",
          }}
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting }) => {
            setAchAccount(values, {
              onSuccess: () => {
                toast.success("Bank details saved");
                onOpenChange(false);
              },
              onError: (error) => {
                toast.error("Failed to save bank details", {
                  description: getErrorMessage(error),
                });
              },
            });
            setSubmitting(false);
          }}
        >
          {({
            values,
            errors,
            isSubmitting,
            isValid,
            handleChange,
            getFieldProps,
            touched,
          }) => (
            <Form className="grid gap-4">
              <DialogHeader>
                <Text variant="title">
                  {overwritingBankDetails
                    ? "Edit banking details"
                    : "Add banking details"}
                </Text>
              </DialogHeader>
              <Stack align="start" className="w-full" gap={6}>
                {overwritingBankDetails && (
                  <>
                    <CurrentBankDetails achAccount={achAccount} />
                    <Separator />
                    <Stack className="w-full" align="start" gap={2}>
                      <Text variant="title">New account details</Text>
                      <Text tone="destructive">
                        Note: This update will replace your current linked bank
                        account in Volca.
                      </Text>
                    </Stack>
                  </>
                )}
                <AutoComplete
                  label="Account type"
                  placeholder="Account type"
                  items={ACCOUNT_TYPE_OPTIONS}
                  selectedValue={values.account_type}
                  onSelectedValueChange={handleChange("account_type")}
                  searchValue={accountTypeSearchValue}
                  onSearchValueChange={setAccountTypeSearchValue}
                  required
                  fullWidth
                />
                <FormInput
                  label="Account number"
                  {...getFieldProps("account_number")}
                  required
                  fullWidth
                />
                <FormInput
                  label="Confirm account number"
                  {...getFieldProps("confirm_account_number")}
                  required
                  fullWidth
                  className={
                    values.confirm_account_number &&
                    values.account_number &&
                    touched.confirm_account_number &&
                    errors.confirm_account_number
                      ? "mb-4"
                      : ""
                  }
                  error={
                    values.confirm_account_number &&
                    values.account_number &&
                    touched.confirm_account_number &&
                    errors.confirm_account_number
                      ? errors.confirm_account_number
                      : undefined
                  }
                />
                <FormInput
                  label="Routing number"
                  {...getFieldProps("routing_number")}
                  required
                  fullWidth
                  maxLength={9}
                />
              </Stack>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                >
                  Save
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

interface CurrentBankDetailsProps {
  achAccount: IAchAccount;
}

const CurrentBankDetails = ({ achAccount }: CurrentBankDetailsProps) => (
  <Stack className="w-full" align="start" gap={4}>
    <Text variant="title">Current bank details</Text>
    <Stack className="w-full" align="start" direction="row" gap={6}>
      <InfoWithLabel label="Account name">{achAccount.name}</InfoWithLabel>
      <InfoWithLabel label="Account number">
        {`*******${achAccount.mask}`}
      </InfoWithLabel>
    </Stack>
  </Stack>
);
